import { CSInterface } from "csinterface-ts";

// Map REACT_APP_HOST_TYPE to environment names
const HOST_TYPE_TO_ENV: Record<string, 'master' | 'dev' | 'local'> = {
  'production': 'master',
  'staging': 'master', // staging builds use master environment
  'development': 'dev',
  'local': 'local'
};

// Environment configuration matching our manifest config
export interface EnvironmentConfig {
  bundleId: string;
  extensionId: string;
  serverId: string;
  menuName: string;
  port: number;
  debugPorts: {
    extension: number;
    server: number;
  };
  environment: 'master' | 'dev' | 'local';
}

export const ENVIRONMENT_CONFIGS: Record<string, EnvironmentConfig> = {
  'com.Blingsio.blingsExt': {
    bundleId: 'com.Blingsio.blingsExt',
    extensionId: 'com.Blingsio.blingsExt.extension1',
    serverId: 'com.Blingsio.blingsExt.server1',
    menuName: 'Blings.io',
    port: 8093,
    debugPorts: {
      extension: 7778,
      server: 7779
    },
    environment: 'master'
  },
  'com.Blingsio.blingsExt.dev': {
    bundleId: 'com.Blingsio.blingsExt.dev',
    extensionId: 'com.Blingsio.blingsExt.dev.extension1',
    serverId: 'com.Blingsio.blingsExt.dev.server1',
    menuName: 'Blings.io (Dev)',
    port: 8094,
    debugPorts: {
      extension: 7780,
      server: 7781
    },
    environment: 'dev'
  },
  'com.Blingsio.blingsExt.local': {
    bundleId: 'com.Blingsio.blingsExt.local',
    extensionId: 'com.Blingsio.blingsExt.local.extension1',
    serverId: 'com.Blingsio.blingsExt.local.server1',
    menuName: 'Blings.io (Local)',
    port: 8095,
    debugPorts: {
      extension: 7782,
      server: 7783
    },
    environment: 'local'
  }
};

export class EnvironmentDetector {
  private static instance: EnvironmentDetector;
  private currentConfig: EnvironmentConfig | null = null;
  private csInterface: CSInterface;

  private constructor(csInterface: CSInterface) {
    this.csInterface = csInterface;
    this.detectEnvironment();
  }

  public static getInstance(csInterface: CSInterface): EnvironmentDetector {
    if (!EnvironmentDetector.instance) {
      EnvironmentDetector.instance = new EnvironmentDetector(csInterface);
    }
    return EnvironmentDetector.instance;
  }

  private detectEnvironment(): void {
    try {
      // Get the current extension ID
      const extensionId = this.csInterface.getExtensionID();
      console.log('Extension ID:', extensionId);
      
      // Extract bundle ID from extension ID (remove the .extension1 or .server1 suffix)
      const bundleId = extensionId.replace(/\.(extension1|server1)$/, '');
      console.log('Detected Bundle ID:', bundleId);
      
      // Find matching environment configuration
      this.currentConfig = ENVIRONMENT_CONFIGS[bundleId] || null;
      
      if (this.currentConfig) {
        console.log(`Environment detected: ${this.currentConfig.environment}`);
        console.log('Environment config:', this.currentConfig);
      } else {
        console.warn('Unknown bundle ID, trying fallback detection...');
        
        // Fallback: try to detect from REACT_APP_HOST_TYPE
        const hostType = process.env.REACT_APP_HOST_TYPE || 'local';
        const envName = HOST_TYPE_TO_ENV[hostType] || 'local';
        
        console.log(`Fallback environment detection: ${envName} (from REACT_APP_HOST_TYPE: ${hostType})`);
        
        // Find config by environment name
        const fallbackConfig = Object.values(ENVIRONMENT_CONFIGS).find(
          config => config.environment === envName
        );

        if (fallbackConfig) {
          this.currentConfig = fallbackConfig;
          console.log('Using fallback environment config:', this.currentConfig);
        } else {
          console.warn('Fallback failed, using local environment');
          this.currentConfig = ENVIRONMENT_CONFIGS['com.Blingsio.blingsExt.local'];
        }
      }
    } catch (error) {
      console.error('Error detecting environment:', error);
      // Ultimate fallback to local environment
      this.currentConfig = ENVIRONMENT_CONFIGS['com.Blingsio.blingsExt.local'];
    }
  }

  public getCurrentConfig(): EnvironmentConfig {
    if (!this.currentConfig) {
      throw new Error('Environment not detected');
    }
    return this.currentConfig;
  }

  public getEnvironment(): 'master' | 'dev' | 'local' {
    return this.getCurrentConfig().environment;
  }

  public getServerId(): string {
    return this.getCurrentConfig().serverId;
  }

  public getExtensionId(): string {
    return this.getCurrentConfig().extensionId;
  }

  public getBundleId(): string {
    return this.getCurrentConfig().bundleId;
  }

  public getPort(): number {
    return this.getCurrentConfig().port;
  }

  public getDebugPorts(): { extension: number; server: number } {
    return this.getCurrentConfig().debugPorts;
  }

  public getMenuName(): string {
    return this.getCurrentConfig().menuName;
  }

  public isDevelopment(): boolean {
    const env = this.getEnvironment();
    return env === 'dev' || env === 'local';
  }

  public isProduction(): boolean {
    return this.getEnvironment() === 'master';
  }
}

// Convenience function to get environment detector instance
export function getEnvironmentDetector(csInterface: CSInterface): EnvironmentDetector {
  return EnvironmentDetector.getInstance(csInterface);
}

// Convenience function to get current environment
export function getCurrentEnvironment(csInterface: CSInterface): 'master' | 'dev' | 'local' {
  return getEnvironmentDetector(csInterface).getEnvironment();
}

// Convenience function to get server ID for current environment
export function getServerIdForCurrentEnvironment(csInterface: CSInterface): string {
  return getEnvironmentDetector(csInterface).getServerId();
}
